import { useNavigate, useParams } from "react-router-dom";
import { DotIcon } from "lucide-react";
import { ROLES } from "../../../../config/constants";
import AdminRoute from "../../../auth/RestrictedRoute";
import BoxWrapper from "../../../../components/BoxWrapper";
import { BookingSvg } from "../../../../components/SideBarMenu";
import RenderTable from "../../../../components/RenderTable";
import { useQueryParam } from "../../../../hooks/useQueryParam";
import { useMultiPlaceAdminPartnerBookingsQuery } from "../../../../hooks/bookings/useBookingQuery";
import { formatCurrency, pluralize } from "../../../../helpers/general";
import Pagination from "../../../../components/Pagination";
import BookingTable from "../../bookings/BookingTable";
import { general } from "../../../../locales/general";
import apiService from "../../../../service/apiService";
import { useCookie } from "../../../../hooks/useCookie";
import { useMutation, useQuery } from "react-query";
import MoneyIcon from "../../../../components/icons/taxiStatsIcons/MoneyIcon";
import PercentIcon from "../../../../components/icons/taxiStatsIcons/PercentIcon";
import WalletIcon from "../../../../components/icons/taxiStatsIcons/WalletIcon";
import { useToast } from "../../../../hooks/useToast";
import { CSpinner } from "@coreui/react";

type CreateInvoicePayload = {
  month: number;
  year: number;
  partnerId: string;
};

const PAGE_SIZE = 10;

export function NewInvoicePage() {
  const params: Readonly<{
    invoiceDate?: string;
    partnerId?: string;
  }> = useParams();
  const { add } = useQueryParam();
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();

  const fetchPlaceManagerInfo = async () => {
    const endpoint = `users/place-manager/${params.partnerId}`;
    return apiService.MakeGetRequest<PlaceManager>(endpoint, token);
  };

  const { data: placeManager } = useQuery<any, any, PlaceManager, any>(
    ["getPlaceManager", params.partnerId],
    () => fetchPlaceManagerInfo(),
    {
      keepPreviousData: true,
    },
  );

  const { bookings, totals, isLoading, totalPages, totalItems, currentPage } =
    useMultiPlaceAdminPartnerBookingsQuery(
      params.partnerId as string,
      params.invoiceDate as string,
    );

  const newInvoiceMutation = useMutation(async (data: CreateInvoicePayload) => {
    return await apiService.MakePostRequest(`invoices/`, data, token, false);
  });

  const handleCreateInvoice = async () => {
    try {
      const data = params?.invoiceDate?.split("-");
      await newInvoiceMutation.mutateAsync(
        {
          year: Number(data?.[0]),
          month: Number(data?.[1]),
          partnerId: params.partnerId as string,
        },
        {
          onError() {
            toastError(general.fr.message.operationFailed);
          },
          onSuccess() {
            toastSuccess(general.fr.message.invoiceCreated);
            navigate("/invoices");
          },
        },
      );
    } catch (error) {
      // do nothing
    }
  };

  return (
    <AdminRoute route={ROLES.PLACE_ADMIN} requireMultiPlaceAdmin={true}>
      <div className="d-flex justify-content-between items-center">
        <div className="d-flex flex-col">
          <h5 className="fs-4 fw-bold mb-2">Nouvelle facture</h5>
          <div>
            Date de fature:{" "}
            <span className="font-semibold">{params.invoiceDate}</span>{" "}
            <DotIcon /> Partenaire:{" "}
            <span className="font-semibold">{placeManager?.fullName}</span>
          </div>
        </div>
        {/* buttons */}
        <section className="d-flex justify-content-center buttons gap-4">
          <button
            className="btn btn-danger red-shadow w-20 px-4 py-2 shadow-primary text-white"
            type="button"
            onClick={() => navigate("/invoices", { replace: true })}
            disabled={newInvoiceMutation.isLoading}
          >
            Annuler
          </button>
          {newInvoiceMutation.isLoading ? (
            <div
              className="btn btn-primary text-white shadow-secondary w-20 px-4 d-flex justify-content-center items-center"
              style={{
                minWidth: "175px",
              }}
            >
              <CSpinner size="sm" color="#ffffff" />
            </div>
          ) : (
            <button
              className="btn btn-primary text-white shadow-secondary w-20 px-4"
              type="button"
              onClick={handleCreateInvoice}
            >
              Créer la facture
            </button>
          )}
        </section>
      </div>
      <div className="pt-4 row">
        <CardStatistics
          title="Revenu"
          total={totals?.totalPrice ?? 0}
          icon={
            (
              <MoneyIcon width="48" height="48" className="text-danger" />
            ) as React.ReactNode
          }
          borderColor="border-danger"
          iconBgColor="bg-danger"
        />
        <CardStatistics
          title={`Commission hôtelière`}
          total={totals?.partnerTotal ?? 0}
          icon={
            (
              <PercentIcon width="48" height="48" className="text-primary" />
            ) as React.ReactNode
          }
          borderColor="border-primary"
          iconBgColor="bg-primary"
        />
        <CardStatistics
          title="Vous"
          total={totals?.adminTotal ?? 0}
          icon={
            (
              <WalletIcon width="48" height="48" className="text-success" />
            ) as React.ReactNode
          }
          borderColor="border-success"
          iconBgColor="bg-success"
        />
      </div>
      <BoxWrapper>
        <div className="d-flex  justify-content-between align-items-baseline before-table">
          <div className="d-flex align-items-center gap-2 p-3 delete-add"></div>
          <div className="d-flex flex-column-reverse flex-md-row  justify-content-end">
            <div className="d-flex align-items-center justify-content-between">
              <div
                className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3 w-100"
                style={{ borderTopRightRadius: "8px" }}
              >
                <BookingSvg />
                <p className="fs-5 fw-bold m-0  text-white">
                  {totalItems} {pluralize("Réservation", totalItems)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <RenderTable
          loading={isLoading}
          render={() =>
            bookings?.length > 0 ? (
              <BookingTable bookings={bookings} />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>
      <Pagination
        currentPage={Number(currentPage)}
        onPageChange={(page) => {
          add("page", page.toString());
        }}
        siblingCount={1}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />
    </AdminRoute>
  );
}

export function CardStatistics({
  title,
  iconBgColor,
  borderColor,
  icon,
  total,
}: {
  title: string;
  total: number;
  icon: React.ReactNode;
  iconBgColor: string;
  borderColor: string;
}) {
  return (
    <div className="col-12 col-sm-6 col-lg-3 ">
      <div
        className={`card rounded-4 overflow-hidden position-relative shadow p-3 mb-4 rounded-5  border ${borderColor}`}
      >
        <div className="card-body p-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div
              className={`rounded-circle ${iconBgColor} bg-opacity-10 p-3 d-flex align-items-center justify-content-center`}
            >
              {icon}
            </div>
            <div className="text-end">
              <h6 className="text-black mb-1 fw-normal">{title}</h6>
              <h3 className="fw-bold mb-0 text-black">
                {formatCurrency(total ?? 0)} MAD
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
